import { useState, useEffect, useCallback, useRef } from 'react';

export interface NetworkStatus {
  isOnline: boolean;
  effectiveType: string;
  downlink: number | null;
  rtt: number | null;
  saveData: boolean;
  connectionQuality: 'excellent' | 'good' | 'poor' | 'offline' | 'unknown';
}

export interface NetworkDiagnostics {
  dnsLatency: number | null;
  httpLatency: number | null;
  downloadSpeed: number | null;
  lastUpdated: Date | null;
  errors: string[];
}

interface UseNetworkStatusOptions {
  enableDiagnostics?: boolean;
  diagnosticsInterval?: number;
  monitoringEnabled?: boolean;
  testEndpoints?: string[];
}

interface NetworkStatusHookReturn {
  networkStatus: NetworkStatus;
  diagnostics: NetworkDiagnostics;
  isMonitoring: boolean;
  startMonitoring: () => void;
  stopMonitoring: () => void;
  runDiagnostics: () => Promise<NetworkDiagnostics>;
  assessConnectionQuality: () => 'excellent' | 'good' | 'poor' | 'offline' | 'unknown';
}

const DEFAULT_TEST_ENDPOINTS = [
  'https://www.google.com/favicon.ico',
  'https://cdn.jsdelivr.net/npm/lodash@1.0.0/package.json', // Fast CDN test
  '/api/health' // Our own health check
];

export function useNetworkStatus(options: UseNetworkStatusOptions = {}): NetworkStatusHookReturn {
  const {
    enableDiagnostics = false,
    diagnosticsInterval = 30000, // 30 seconds
    monitoringEnabled = true,
    testEndpoints = DEFAULT_TEST_ENDPOINTS
  } = options;

  const [networkStatus, setNetworkStatus] = useState<NetworkStatus>({
    isOnline: navigator.onLine,
    effectiveType: 'unknown',
    downlink: null,
    rtt: null,
    saveData: false,
    connectionQuality: 'unknown'
  });

  const [diagnostics, setDiagnostics] = useState<NetworkDiagnostics>({
    dnsLatency: null,
    httpLatency: null,
    downloadSpeed: null,
    lastUpdated: null,
    errors: []
  });

  const [isMonitoring, setIsMonitoring] = useState(false);
  const monitoringRef = useRef<number | null>(null);
  const diagnosticsRef = useRef<number | null>(null);

  // Get network connection info (if available)
  const getConnectionInfo = useCallback(() => {
    if ('connection' in navigator) {
      const connection = (navigator as any).connection;
      return {
        effectiveType: connection.effectiveType || 'unknown',
        downlink: connection.downlink || null,
        rtt: connection.rtt || null,
        saveData: connection.saveData || false
      };
    }
    return {
      effectiveType: 'unknown',
      downlink: null,
      rtt: null,
      saveData: false
    };
  }, []);

  // Assess connection quality based on available metrics
  const assessConnectionQuality = useCallback((): 'excellent' | 'good' | 'poor' | 'offline' | 'unknown' => {
    if (!navigator.onLine) return 'offline';

    const connection = getConnectionInfo();
    
    // Use effective type if available
    if (connection.effectiveType !== 'unknown') {
      switch (connection.effectiveType) {
        case '4g':
          return connection.downlink && connection.downlink > 10 ? 'excellent' : 'good';
        case '3g':
          return 'good';
        case '2g':
        case 'slow-2g':
          return 'poor';
        default:
          return 'good';
      }
    }

    // Use RTT if available
    if (connection.rtt !== null) {
      if (connection.rtt < 50) return 'excellent';
      if (connection.rtt < 200) return 'good';
      return 'poor';
    }

    // Use downlink if available
    if (connection.downlink !== null) {
      if (connection.downlink > 10) return 'excellent';
      if (connection.downlink > 1.5) return 'good';
      return 'poor';
    }

    return 'unknown';
  }, [getConnectionInfo]);

  // Update network status
  const updateNetworkStatus = useCallback(() => {
    const connectionInfo = getConnectionInfo();
    const quality = assessConnectionQuality();

    setNetworkStatus({
      isOnline: navigator.onLine,
      ...connectionInfo,
      connectionQuality: quality
    });
  }, [getConnectionInfo, assessConnectionQuality]);

  // Test DNS latency
  const testDNSLatency = useCallback(async (): Promise<number | null> => {
    try {
      const start = performance.now();
      await fetch('https://dns.google/resolve?name=example.com&type=A', {
        method: 'GET',
        mode: 'cors',
        cache: 'no-cache'
      });
      return performance.now() - start;
    } catch (error) {
      console.warn('DNS latency test failed:', error);
      return null;
    }
  }, []);

  // Test HTTP latency and download speed
  const testHTTPLatency = useCallback(async (endpoint: string): Promise<{ latency: number; speed: number } | null> => {
    try {
      const start = performance.now();
      const response = await fetch(endpoint, {
        method: 'HEAD',
        cache: 'no-cache',
        signal: AbortSignal.timeout(10000) // 10 second timeout
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const latency = performance.now() - start;
      
      // For speed test, try to get a small file
      const speedTestStart = performance.now();
      const speedResponse = await fetch(endpoint.replace('/favicon.ico', '/favicon.ico'), {
        cache: 'no-cache',
        signal: AbortSignal.timeout(5000)
      });
      
      if (speedResponse.ok) {
        const blob = await speedResponse.blob();
        const speedTestEnd = performance.now();
        const duration = (speedTestEnd - speedTestStart) / 1000; // Convert to seconds
        const speed = blob.size / duration; // bytes per second
        
        return { latency, speed };
      }

      return { latency, speed: 0 };
    } catch (error) {
      console.warn(`HTTP test failed for ${endpoint}:`, error);
      return null;
    }
  }, []);

  // Run comprehensive network diagnostics
  const runDiagnostics = useCallback(async (): Promise<NetworkDiagnostics> => {
    const errors: string[] = [];
    let dnsLatency: number | null = null;
    let httpLatency: number | null = null;
    let downloadSpeed: number | null = null;

    try {
      // Test DNS latency
      dnsLatency = await testDNSLatency();
      if (dnsLatency === null) {
        errors.push('DNS resolution test failed');
      }
    } catch (error) {
      errors.push(`DNS test error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }

    // Test HTTP endpoints
    const httpResults = [];
    for (const endpoint of testEndpoints) {
      try {
        const result = await testHTTPLatency(endpoint);
        if (result) {
          httpResults.push(result);
        } else {
          errors.push(`HTTP test failed for ${endpoint}`);
        }
      } catch (error) {
        errors.push(`HTTP test error for ${endpoint}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }

    // Calculate averages from successful tests
    if (httpResults.length > 0) {
      httpLatency = httpResults.reduce((sum, result) => sum + result.latency, 0) / httpResults.length;
      downloadSpeed = httpResults.reduce((sum, result) => sum + result.speed, 0) / httpResults.length;
    } else {
      errors.push('All HTTP tests failed');
    }

    const newDiagnostics = {
      dnsLatency,
      httpLatency,
      downloadSpeed,
      lastUpdated: new Date(),
      errors
    };

    setDiagnostics(newDiagnostics);
    return newDiagnostics;
  }, [testDNSLatency, testHTTPLatency, testEndpoints]);

  // Start monitoring
  const startMonitoring = useCallback(() => {
    if (isMonitoring) return;

    setIsMonitoring(true);

    // Initial update
    updateNetworkStatus();

    if (!monitoringEnabled) return;

    // Set up periodic status updates
    monitoringRef.current = window.setInterval(() => {
      updateNetworkStatus();
    }, 5000); // Update every 5 seconds

    // Set up diagnostics if enabled
    if (enableDiagnostics) {
      // Initial diagnostics
      runDiagnostics();
      
      // Periodic diagnostics
      diagnosticsRef.current = window.setInterval(() => {
        runDiagnostics();
      }, diagnosticsInterval);
    }
  }, [isMonitoring, monitoringEnabled, updateNetworkStatus, enableDiagnostics, runDiagnostics, diagnosticsInterval]);

  // Stop monitoring
  const stopMonitoring = useCallback(() => {
    setIsMonitoring(false);

    if (monitoringRef.current) {
      clearInterval(monitoringRef.current);
      monitoringRef.current = null;
    }

    if (diagnosticsRef.current) {
      clearInterval(diagnosticsRef.current);
      diagnosticsRef.current = null;
    }
  }, []);

  // Set up event listeners
  useEffect(() => {
    const handleOnline = () => updateNetworkStatus();
    const handleOffline = () => updateNetworkStatus();
    const handleConnectionChange = () => updateNetworkStatus();

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Listen for connection changes if supported
    if ('connection' in navigator) {
      (navigator as any).connection.addEventListener('change', handleConnectionChange);
    }

    // Initial status update
    updateNetworkStatus();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      
      if ('connection' in navigator) {
        try {
          (navigator as any).connection.removeEventListener('change', handleConnectionChange);
        } catch (error) {
          // Ignore cleanup errors
        }
      }
    };
  }, [updateNetworkStatus]);

  // Auto-start monitoring if enabled
  useEffect(() => {
    if (monitoringEnabled) {
      startMonitoring();
    }

    return () => {
      stopMonitoring();
    };
  }, [monitoringEnabled, startMonitoring, stopMonitoring]);

  return {
    networkStatus,
    diagnostics,
    isMonitoring,
    startMonitoring,
    stopMonitoring,
    runDiagnostics,
    assessConnectionQuality
  };
} 